import React, { useCallback, useMemo, useRef, useState } from "react"
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image, ImageStyle } from "expo-image"

import { Text } from "src/components/Ignite/Text"
import LinearGradient from "src/components/LinearGradient"
import { avatarImageMap } from "src/context/RiveContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { ContentLibraryItem } from "./adapters"
import { SortKey } from "./contentLibraryUtils"

export type { SortKey } from "./contentLibraryUtils"
export { sortAndFilterItems } from "./contentLibraryUtils"

interface ContentLibrarySortBarProps {
  items: ContentLibraryItem[]
  activeSort: SortKey
  activeSidekick: string | null
  activeTopic: string | null
  activeType: string | null
  onSortChange: (sort: SortKey) => void
  onSubFilterChange: (filter: {
    sidekick?: string | null
    topic?: string | null
    type?: string | null
  }) => void
  /** Called when the search icon button is pressed */
  onSearchPress?: () => void
  /** Whether the search bar is currently expanded */
  searchActive?: boolean
}

/**
 * Pill-row sort bar with Latest / Sidekick / Topic / Type primary sort buttons.
 * When a grouping sort is active a scrollable sub-filter row shows the distinct values.
 * Topic is hidden when no items have a topic field.
 * A search icon on the right toggles the search bar open/closed.
 */
export function ContentLibrarySortBar({
  items,
  activeSort,
  activeSidekick,
  activeTopic,
  activeType,
  onSortChange,
  onSubFilterChange,
  onSearchPress,
  searchActive = false,
}: ContentLibrarySortBarProps) {
  const { themed, theme } = useAppTheme()
  const { colors } = theme

  const hasTopics = useMemo(() => items.some((i) => !!i.topic), [items])

  const sidekicks = useMemo(
    () => Array.from(new Set(items.map((i) => i.sidekickName))).filter(Boolean),
    [items],
  )
  const topics = useMemo(
    () => Array.from(new Set(items.map((i) => i.topic).filter(Boolean))) as string[],
    [items],
  )
  const types = useMemo(
    () => Array.from(new Set(items.map((i) => i.mediaType).filter(Boolean))) as string[],
    [items],
  )

  // Map sidekick name → avatar image source (first match per name wins)
  const sidekickAvatarMap = useMemo(() => {
    const map: Record<string, ReturnType<(typeof avatarImageMap)[keyof typeof avatarImageMap]>> = {}
    for (const item of items) {
      if (item.sidekickName && !map[item.sidekickName]) {
        const key = item.sidekickAvatarId?.padStart(2, "0") ?? "00"
        map[item.sidekickName] = avatarImageMap[key] ?? avatarImageMap["00"]
      }
    }
    return map
  }, [items])

  const primaryButtons: { key: SortKey; label: string; visible: boolean }[] = [
    { key: "latest", label: "Latest", visible: true },
    { key: "sidekick", label: "Sidekick", visible: sidekicks.length > 1 },
    { key: "topic", label: "Topic", visible: hasTopics },
    { key: "type", label: "Type", visible: types.length > 1 },
  ]

  return (
    <View>
      {/* Primary sort row + search icon */}
      <View style={styles.primaryRowWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.primaryRow}
        >
          {primaryButtons
            .filter((b) => b.visible)
            .map((btn) => (
              <SortPill
                key={btn.key}
                label={btn.label}
                active={!searchActive && activeSort === btn.key}
                onPress={() => onSortChange(btn.key)}
              />
            ))}
        </ScrollView>

        {onSearchPress && (
          <TouchableOpacity
            onPress={onSearchPress}
            style={themed(searchActive ? $searchButtonActive : $searchButton)}
            accessibilityRole="button"
            accessibilityLabel={searchActive ? "Close search" : "Search"}
          >
            <MaterialIcons
              name="search"
              size={20}
              color={searchActive ? colors.white : colors.text}
            />
          </TouchableOpacity>
        )}
      </View>

      {/* Sidekick sub-filter row — avatar pill per sidekick, tap again to deselect */}
      {activeSort === "sidekick" && sidekicks.length > 0 && (
        <FadeScrollRow contentContainerStyle={styles.subRow}>
          {sidekicks.map((s) => (
            <SidekickPill
              key={s}
              name={s}
              avatarSource={sidekickAvatarMap[s]}
              active={activeSidekick === s}
              onPress={() => onSubFilterChange({ sidekick: activeSidekick === s ? null : s })}
            />
          ))}
        </FadeScrollRow>
      )}

      {/* Topic sub-filter row */}
      {activeSort === "topic" && topics.length > 0 && (
        <FadeScrollRow contentContainerStyle={styles.subRow}>
          <SortPill
            label="All"
            active={activeTopic === null}
            onPress={() => onSubFilterChange({ topic: null })}
            small
          />
          {topics.map((t) => (
            <SortPill
              key={t}
              label={t}
              active={activeTopic === t}
              onPress={() => onSubFilterChange({ topic: t })}
              small
            />
          ))}
        </FadeScrollRow>
      )}

      {/* Type sub-filter row — icon + label pill per media type, tap again to deselect */}
      {activeSort === "type" && types.length > 0 && (
        <FadeScrollRow contentContainerStyle={styles.subRow}>
          {types.map((t) => (
            <TypePill
              key={t}
              type={t}
              active={activeType === t}
              onPress={() => onSubFilterChange({ type: activeType === t ? null : t })}
            />
          ))}
        </FadeScrollRow>
      )}
    </View>
  )
}

const MEDIA_TYPE_LABEL: Record<string, string> = {
  video: "Video",
  pdf: "PDF",
  audio: "Audio",
  image: "Image",
  url: "Website",
  text: "Text",
  youtube: "YouTube",
}

const MEDIA_TYPE_ICON: Record<string, React.ComponentProps<typeof MaterialIcons>["name"]> = {
  video: "videocam",
  audio: "headset",
  pdf: "picture-as-pdf",
  image: "image",
  url: "language",
  text: "article",
  youtube: "videocam",
}

// ---------------------------------------------------------------------------
// Horizontal scroll row with left/right fade affordance
// ---------------------------------------------------------------------------

interface FadeScrollRowProps {
  contentContainerStyle?: ViewStyle
  children: React.ReactNode
}

function FadeScrollRow({ contentContainerStyle, children }: FadeScrollRowProps) {
  const { theme } = useAppTheme()
  const bg = theme.colors.background

  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)
  const contentWidthRef = useRef(0)
  const containerWidthRef = useRef(0)

  const updateFades = useCallback((scrollX: number) => {
    setShowLeft(scrollX > 2)
    setShowRight(scrollX + containerWidthRef.current < contentWidthRef.current - 2)
  }, [])

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      updateFades(e.nativeEvent.contentOffset.x)
    },
    [updateFades],
  )

  const handleContentSizeChange = useCallback(
    (w: number) => {
      contentWidthRef.current = w
      updateFades(0)
    },
    [updateFades],
  )

  const handleLayout = useCallback(
    (e: { nativeEvent: { layout: { width: number } } }) => {
      containerWidthRef.current = e.nativeEvent.layout.width
      updateFades(0)
    },
    [updateFades],
  )

  const fadeWidth = 32

  return (
    <View style={styles.fadeRowWrapper}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={true}
        contentContainerStyle={contentContainerStyle}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
      >
        {children}
      </ScrollView>

      {showLeft && (
        <LinearGradient
          colors={[bg, "transparent"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.fadeEdge, { left: 0, width: fadeWidth }]}
          pointerEvents="none"
        />
      )}
      {showRight && (
        <LinearGradient
          colors={["transparent", bg]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.fadeEdge, { right: 0, width: fadeWidth }]}
          pointerEvents="none"
        />
      )}
    </View>
  )
}

// ---------------------------------------------------------------------------
// Sub-filter pill components
// ---------------------------------------------------------------------------

interface SortPillProps {
  label: string
  active: boolean
  onPress: () => void
  small?: boolean
}

function SortPill({ label, active, onPress, small }: SortPillProps) {
  const { themed } = useAppTheme()
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[themed(active ? $pillActive : $pill), small && styles.pillSmall]}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
    >
      <Text style={themed(active ? $pillTextActive : $pillText)}>{label}</Text>
    </TouchableOpacity>
  )
}

interface SidekickPillProps {
  name: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  avatarSource: any
  active: boolean
  onPress: () => void
}

function SidekickPill({ name, avatarSource, active, onPress }: SidekickPillProps) {
  const { themed } = useAppTheme()
  return (
    <TouchableOpacity
      onPress={onPress}
      style={styles.sidekickItem}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
    >
      {/* Ring wrapper creates a visible gap between the avatar and the selection border */}
      <View style={themed(active ? $sidekickAvatarRingActive : $sidekickAvatarRing)}>
        <Image source={avatarSource} style={themed($sidekickAvatar)} />
      </View>
      <Text style={themed(active ? $sidekickNameActive : $sidekickName)} numberOfLines={1}>
        {name}
      </Text>
    </TouchableOpacity>
  )
}

interface TypePillProps {
  type: string
  active: boolean
  onPress: () => void
}

function TypePill({ type, active, onPress }: TypePillProps) {
  const { themed, theme } = useAppTheme()
  const iconColor = active ? theme.colors.white : theme.colors.text
  const iconName = MEDIA_TYPE_ICON[type] ?? "insert-drive-file"
  return (
    <TouchableOpacity
      onPress={onPress}
      style={themed(active ? $typePillActive : $typePillInactive)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
    >
      <MaterialIcons name={iconName} size={15} color={iconColor} />
      <Text style={themed(active ? $pillTextActive : $pillText)}>
        {MEDIA_TYPE_LABEL[type] ?? type}
      </Text>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  fadeRowWrapper: {
    position: "relative",
  },
  fadeEdge: {
    position: "absolute",
    top: 0,
    bottom: 0,
  },
  primaryRowWrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  primaryRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
  },
  subRow: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingBottom: 8,
    gap: 6,
    alignItems: "flex-start",
  },
  pillSmall: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  sidekickItem: {
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
})

const $searchButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: 36,
  height: 36,
  borderRadius: 18,
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  backgroundColor: colors.pillUnselected,
})

const $searchButtonActive: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: 36,
  height: 36,
  borderRadius: 18,
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  backgroundColor: colors.pillSelected,
})

const $pill: ThemedStyle<ViewStyle> = ({ colors }) => ({
  paddingHorizontal: 14,
  paddingVertical: 6,
  borderRadius: 20,
  backgroundColor: colors.pillUnselected,
})

const $pillActive: ThemedStyle<ViewStyle> = ({ colors }) => ({
  paddingHorizontal: 14,
  paddingVertical: 6,
  borderRadius: 20,
  backgroundColor: colors.pillSelected,
})

const $pillText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 13,
  fontFamily: typography.primary.normal,
  color: colors.text,
})

const $pillTextActive: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 13,
  fontFamily: typography.primary.medium,
  color: colors.white,
})

// Sidekick items: circular avatar + name underneath.
// Mirrors the ConversationHeader pattern: active gets activeAvatarBackground +
// activeAvatarBorder; inactive gets avatarBackground for both so the border
// blends into itself and the avatar simply sits on the subdued background.
const $sidekickAvatarRing: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: 48,
  height: 48,
  borderRadius: 24,
  borderWidth: 2,
  borderColor: colors.avatarBackground,
  backgroundColor: colors.avatarBackground,
  overflow: "hidden",
})

const $sidekickAvatarRingActive: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: 48,
  height: 48,
  borderRadius: 24,
  borderWidth: 2,
  borderColor: colors.activeAvatarBorder,
  backgroundColor: colors.activeAvatarBackground,
  overflow: "hidden",
})

const $sidekickAvatar: ThemedStyle<ImageStyle> = () => ({
  width: "100%",
  height: "100%",
})

const $sidekickName: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 11,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
  textAlign: "center",
  marginTop: 4,
  maxWidth: 56,
})

const $sidekickNameActive: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 11,
  fontFamily: typography.primary.medium,
  color: colors.pillSelected,
  textAlign: "center",
  marginTop: 4,
  maxWidth: 56,
})

// Shared pill shape for type items
const $typePillBase: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  borderRadius: 20,
  paddingVertical: 5,
  paddingHorizontal: 11,
  gap: 5,
}

const $typePillInactive: ThemedStyle<ViewStyle> = ({ colors }) => ({
  ...$typePillBase,
  backgroundColor: colors.pillUnselected,
})

const $typePillActive: ThemedStyle<ViewStyle> = ({ colors }) => ({
  ...$typePillBase,
  backgroundColor: colors.pillSelected,
})
